import { useGuideDrag } from '../use-guide-drag'
import { useRulerGuideCreate } from '../use-ruler-guide-create'
import { useRulerTicks } from '../use-ruler-ticks'
import type { CanvasEditModes, CanvasFoundation, CanvasViewport } from '../../types/canvas-groups'
import type { ResolvedCenterCanvasConfig } from '../../types/resolved-center-canvas-config'

/**
 * Rulers and ruler guides: the visible ruler ticks, dragging existing guides, and pulling a new
 * guide out of a ruler.
 */
export function useCanvasRulerGuides(
  {
    showGuides,
    rulerUnit,
    bleedWidth,
    bleedHeight,
    onBackgroundClick,
    onShowGuidesRestore,
  }: ResolvedCenterCanvasConfig,
  {
    scrollRef,
    pasteboardRef,
    documentCanvasRef,
    updateGuides,
    guidesRef,
    setLocalDragGuides,
    localDragGuidesRef,
    updateGuidesRef,
    setSelectedGuideIds,
    selectedGuideIdsRef,
  }: Pick<
    CanvasFoundation,
    | 'scrollRef'
    | 'pasteboardRef'
    | 'documentCanvasRef'
    | 'updateGuides'
    | 'guidesRef'
    | 'setLocalDragGuides'
    | 'localDragGuidesRef'
    | 'updateGuidesRef'
    | 'setSelectedGuideIds'
    | 'selectedGuideIdsRef'
  >,
  {
    scale,
    verticalRulerZeroAtCanvasY,
    pasteboardSize,
    snappedSpreadOriginCanvasX,
    getCanvasOrigin,
    rulerViewport,
    setRulerViewport,
    syncRulerScrollTransforms,
    rulerContentOrigin,
    pasteboardScrollExtents,
  }: Pick<
    CanvasViewport,
    | 'scale'
    | 'verticalRulerZeroAtCanvasY'
    | 'pasteboardSize'
    | 'snappedSpreadOriginCanvasX'
    | 'getCanvasOrigin'
    | 'rulerViewport'
    | 'setRulerViewport'
    | 'syncRulerScrollTransforms'
    | 'rulerContentOrigin'
    | 'pasteboardScrollExtents'
  >,
  { clearShapeSelectionWithHistory }: Pick<CanvasEditModes, 'clearShapeSelectionWithHistory'>
) {
  const { rulerTicks, visibleRulerTicksH, visibleRulerTicksV } = useRulerTicks({
    scrollRef,
    documentCanvasRef,
    pasteboardRef,
    scale,
    pasteboardSize,
    rulerUnit,
    rulerContentOrigin,
    pasteboardScrollExtents,
    snappedSpreadOriginCanvasX,
    verticalRulerZeroAtCanvasY,
    rulerViewport,
    setRulerViewport,
    syncRulerScrollTransforms,
  })

  const { handleGuideDragStart } = useGuideDrag({
    guidesRef,
    selectedGuideIdsRef,
    setSelectedGuideIds,
    localDragGuidesRef,
    setLocalDragGuides,
    updateGuidesRef,
    clearShapeSelectionWithHistory,
    onBackgroundClick,
    scale,
    bleedWidth,
    bleedHeight,
  })

  const { draggingGuide, handleRulerMouseDown } = useRulerGuideCreate({
    scrollRef,
    getCanvasOrigin,
    scale,
    showGuides,
    onShowGuidesRestore,
    updateGuides,
    setSelectedGuideIds,
  })

  return {
    rulerTicks,
    visibleRulerTicksH,
    visibleRulerTicksV,
    handleGuideDragStart,
    draggingGuide,
    handleRulerMouseDown,
  }
}
